/**
 * Anthropic Claude API client.
 *
 * Two modes, selected at build time:
 *
 *   1. Direct mode — browser posts to /anthropic/v1/messages. setupProxy.js
 *      forwards to api.anthropic.com. REACT_APP_ANTHROPIC_API_KEY (from .env)
 *      is sent as the x-api-key header.
 *
 *   2. Function mode — browser posts to /api/anthropic. The SWA-managed Azure
 *      Function holds ANTHROPIC_API_KEY server-side and forwards to Anthropic.
 *      No key in the browser bundle.
 *
 * Mode selection:
 *   - REACT_APP_USE_DIRECT_ANTHROPIC === 'true'    → force direct (override)
 *   - REACT_APP_USE_DIRECT_ANTHROPIC === 'false'   → force function (override)
 *   - NODE_ENV === 'development' (no flag)         → direct (CRA's `npm start`)
 *   - NODE_ENV === 'production'  (no flag)         → function (CRA's `npm run build`)
 *
 * WARNING: Never set REACT_APP_ANTHROPIC_API_KEY in a production build env —
 * the key would be baked into the published JS bundle. It's for local dev only.
 */

const API_KEY = process.env.REACT_APP_ANTHROPIC_API_KEY;

// Mode selection:
//   - If REACT_APP_USE_DIRECT_ANTHROPIC is set explicitly ('true' or 'false'),
//     it wins — lets you test the Function path locally or the direct path in prod.
//   - Otherwise, default to direct in dev, function in prod.
const USE_DIRECT = (() => {
  const flag = process.env.REACT_APP_USE_DIRECT_ANTHROPIC;
  if (flag === 'true')  return true;
  if (flag === 'false') return false;
  return process.env.NODE_ENV === 'development';
})();

const ENDPOINT = USE_DIRECT ? '/anthropic/v1/messages' : '/api/anthropic';

// eslint-disable-next-line no-console
console.info(
  `%c[anthropic client] mode=${USE_DIRECT ? 'direct' : 'function'} → ${ENDPOINT}`,
  'color:#009688;font-weight:600',
);

/**
 * Sends a single-turn user prompt to the Claude API.
 *
 * @param {Object} options
 * @param {string} options.model      - Claude model ID
 * @param {number} options.maxTokens  - Max tokens in response
 * @param {string} options.prompt     - User prompt text
 * @returns {Promise<string>}         - Response text content
 * @throws {Error}                    - On network failure or non-2xx response
 */
export async function callClaude({ model = 'claude-sonnet-4-6', maxTokens = 500, prompt }) {
  if (USE_DIRECT && !API_KEY) {
    throw new Error(
      'REACT_APP_ANTHROPIC_API_KEY is not set. Add it to .env for local dev, or run the Azure Function host.',
    );
  }

  const headers = { 'content-type': 'application/json' };
  let body;

  if (USE_DIRECT) {
    headers['x-api-key']         = API_KEY;
    headers['anthropic-version'] = '2023-06-01';
    body = JSON.stringify({
      model,
      max_tokens: maxTokens,
      messages: [{ role: 'user', content: prompt }],
    });
  } else {
    body = JSON.stringify({ model, maxTokens, prompt });
  }

  const response = await fetch(ENDPOINT, { method: 'POST', headers, body });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error?.message || `Anthropic API error ${response.status}`);
  }

  const data = await response.json();
  return data.content?.[0]?.text ?? '';
}
